import {
  Backdrop,
  Box,
  CircularProgress,
  Container,
  Divider,
  Grid2,
  LinearProgress,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import { useStores } from "../../../../use-stores";
import { useNotification } from "../../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../../models/Employees/EmployeeRoles";
import CustomerHeading from "../../../components/projects/projectDetails/CustomerHeading";
import CustomTypography from "../../../components/common/form/CustomTypography";
import InventoryOpsTable from "../../../components/projects/inventory-ops/InventoryOpsTable";
import dayjs, { Dayjs } from "dayjs";
import { ProjectInventoryOpsProduct } from "../../../../models/ProjectManagement/ProjectInventoryProduct";
import CommonExpectedDatesDialog from "../../../components/projects/inventory-ops/CommonExpectedDatesDialog";
import PermissionBlockLabel from "../../../components/common/pop-up/PermissionBlockLabel ";

const InventoryOps = () => {
  const theme = useTheme();
  const { userStore, orderStore, projectInventoryOpsStore } = useStores();
  const { showError } = useNotification();
  const params = useParams();
  const navigate = useNavigate();
  const projectId = Number(params["project-id"]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getProjectInventoryOpsProduct(projectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const getProjectInventoryOpsProduct = (projectId: number) => {
    setIsLoading(true);
    projectInventoryOpsStore
      .getProjectInventoryOpsDetails(projectId)
      .finally(() => setIsLoading(false));
  };

  const handlePencilClick = (productId: number, taskName: string) => {
    const selectedProduct =
      projectInventoryOpsStore.inventoryRequestBody?.products?.find(
        (product) => product.id === productId
      );
    if (selectedProduct) {
      projectInventoryOpsStore.setCurrentProductId(productId);
      if (taskName === "raw_inward") {
        projectInventoryOpsStore.setDialogType("rawInward");
      } else if (taskName === "print_inward") {
        projectInventoryOpsStore.setDialogType("printInward");
      }
    }
  };

  const handleCloseDialog = () => {
    projectInventoryOpsStore.setDialogType(null);
    projectInventoryOpsStore.setCurrentProductId(null);
  };

  const updateDate = (
    newValue: Dayjs | null,
    index: number,
    type: "rawInward" | "printInward"
  ) => {
    if (type === "rawInward") {
      projectInventoryOpsStore.updateRawInwardDate(newValue, index);
    } else if (type === "printInward") {
      projectInventoryOpsStore.updatePrintInwardDate(newValue, index);
    }
  };

  const handleRawInwardUpdate = async () => {
    const productId = projectInventoryOpsStore.currentProductId;
    if (!productId) {
      showError("Error", "No product selected to update.");
      return;
    }

    const selectedProduct =
      projectInventoryOpsStore.inventoryRequestBody?.products?.find(
        (product) => product.id === productId
      );

    if (!selectedProduct || !selectedProduct.purchase_orders) {
      showError("Error", "No purchase order data available to update.");
      return;
    }

    const poData = selectedProduct.purchase_orders;

    handleCloseDialog();

    const orderId =
      projectInventoryOpsStore.inventoryRequestBody.products?.[0]?.order_id;

    if (!orderId) {
      showError("Error", "Order ID is missing.");
      return;
    }

    const formattedPoData = poData.map((po) => ({
      ...po,
      expected_delivery_date: po.expected_delivery_date
        ? dayjs(po.expected_delivery_date).format("YYYY-MM-DD 00:00:00")
        : undefined,
    }));

    projectInventoryOpsStore
      .updateRawInwardExpectedDate(orderId, productId, formattedPoData)
      .catch((error) => {
        const errorMessage =
          (error as any)?.response?.data?.message ||
          "Failed to update expected dates.";
        showError("Error", errorMessage);
      });
  };

  const handlePrintInwardUpdate = async () => {
    const productId = projectInventoryOpsStore.currentProductId;
    if (!productId) {
      showError("Error", "No product selected to update.");
      return;
    }

    const selectedProduct =
      projectInventoryOpsStore.inventoryRequestBody?.products?.find(
        (product) => product.id === productId
      );

    if (!selectedProduct || !selectedProduct.print_inward_timelines) {
      showError("Error", "No print inward data available to update.");
      return;
    }

    const piData = selectedProduct.print_inward_timelines;

    handleCloseDialog();

    const orderId =
      projectInventoryOpsStore.inventoryRequestBody.products?.[0]?.order_id;

    if (!orderId) {
      showError("Error", "Order ID is missing.");
      return;
    }

    const formattedPiData = piData.map((timeline) => ({
      raw_inward_id: timeline.raw_inward_id,
      po_id: timeline.po_id,
      timeline: timeline.timeline
        ? dayjs(timeline.timeline).format("YYYY-MM-DD 00:00:00")
        : undefined,
    }));

    projectInventoryOpsStore
      .updatePrintInwardTimeline(orderId, productId, formattedPiData)
      .catch((error) => {
        const errorMessage =
          (error as any)?.response?.data?.message ||
          "Failed to update print inward timeline.";
        showError("Error", errorMessage);
      });
  };

  const handleNavigateByProductId = (id: number) => {
    const product =
      projectInventoryOpsStore?.inventoryRequestBody?.products?.filter(
        (prod: ProjectInventoryOpsProduct) => prod.id === id
      );
    if (product && product[0]?.purchase_orders.length === 0) {
      showError("Error", "No Purchase Order found for this product.");
    } else {
      navigate(
        `/projects/inventory-ops-details?project-id=${projectId}&product-id=${id}`
      );
    }
  };

  const getSelectedProduct = () => {
    if (!projectInventoryOpsStore.currentProductId) return null;
    return (
      projectInventoryOpsStore.inventoryRequestBody?.products?.find(
        (product) => product.id === projectInventoryOpsStore.currentProductId
      ) || null
    );
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      {isLoading ? (
        <Backdrop
          sx={(theme) => ({
            color: "#fff",
            zIndex: theme.zIndex.drawer + 1,
          })}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : (
        <>
          {userStore.hasPermission(EmployeeRoles.VIEW_ORDER) ? (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 5,
                }}
              >
                <Box>
                  <CustomerHeading
                    label={orderStore.orderBasicDetails?.label || ""}
                    orderId={projectId}
                    moduleName="Inventory Ops"
                    brandName={
                      orderStore.orderBasicDetails?.customer?.name || ""
                    }
                    progressValue={
                      projectInventoryOpsStore.inventoryRequestBody
                        .total_progress || 0
                    }
                  />
                </Box>
                <Box
                  sx={{
                    paddingBottom: "1.5em",
                    color: theme.palette.customColors.greenDark,
                  }}
                >
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box>
                      <CustomTypography
                        variant="heading7"
                        sx={{ marginBottom: 1 }}
                        color={theme.palette.customColors.greyTextColor}
                      >
                        Raw Inward QC
                      </CustomTypography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LinearProgress
                          variant="determinate"
                          value={
                            projectInventoryOpsStore.inventoryRequestBody
                              .raw_inward_qc_percentage || 0
                          }
                          sx={{
                            height: 10,
                            width: 200,
                            borderRadius: 5,
                            backgroundColor: "#f2f2f2",
                            "& .MuiLinearProgress-bar": {
                              backgroundColor:
                                theme.palette.customColors.purple,
                            },
                          }}
                        />
                        <CustomTypography
                          variant="heading7"
                          color={theme.palette.customColors.greyTextColor}
                        >
                          {projectInventoryOpsStore.inventoryRequestBody
                            .raw_inward_qc_percentage || 0}
                          %{" "}
                        </CustomTypography>
                      </Box>
                    </Box>
                    <Box>
                      <CustomTypography
                        variant="heading7"
                        sx={{ marginBottom: 1 }}
                        color={theme.palette.customColors.greyTextColor}
                      >
                        Print Inward QC
                      </CustomTypography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LinearProgress
                          variant="determinate"
                          value={
                            projectInventoryOpsStore.inventoryRequestBody
                              .print_inward_qc_percentage || 0
                          }
                          sx={{
                            height: 10,
                            width: 200,
                            borderRadius: 5,
                            backgroundColor: "#f2f2f2",
                            "& .MuiLinearProgress-bar": {
                              backgroundColor:
                                theme.palette.customColors.purple,
                            },
                          }}
                        />
                        <CustomTypography
                          variant="heading7"
                          color={theme.palette.customColors.greyTextColor}
                        >
                          {projectInventoryOpsStore.inventoryRequestBody
                            .print_inward_qc_percentage || 0}
                          %{" "}
                        </CustomTypography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Divider sx={{ mt: 2 }} />

              {/* Common Dialog for Raw Inward */}
              <CommonExpectedDatesDialog
                open={projectInventoryOpsStore.dialogType === "rawInward"}
                onClose={handleCloseDialog}
                title="Raw Inward Expected Dates"
                data={getSelectedProduct()?.purchase_orders || []}
                image={getSelectedProduct()?.image_url}
                isLoading={isLoading}
                onUpdate={handleRawInwardUpdate}
                onDateChange={(newValue, index) =>
                  updateDate(newValue, index, "rawInward")
                }
                type="rawInward"
              />

              {/* Common Dialog for Print Inward */}
              <CommonExpectedDatesDialog
                open={projectInventoryOpsStore.dialogType === "printInward"}
                onClose={handleCloseDialog}
                title="Print Inward Expected Dates"
                data={getSelectedProduct()?.print_inward_timelines || []}
                image={getSelectedProduct()?.image_url}
                isLoading={isLoading}
                onUpdate={handlePrintInwardUpdate}
                onDateChange={(newValue, index) =>
                  updateDate(newValue, index, "printInward")
                }
                type="printInward"
              />

              <InventoryOpsTable
                maxHeight={window.innerHeight - 350}
                handleNavigateById={handleNavigateByProductId}
                products={
                  projectInventoryOpsStore.inventoryRequestBody.products || []
                }
                handlePencilClick={handlePencilClick}
                isLoading={isLoading}
                archived={
                  projectInventoryOpsStore.inventoryRequestBody.archived
                }
              />
            </Box>
          ) : (
            <PermissionBlockLabel />
          )}
        </>
      )}
    </Grid2>
  );
};

export default observer(InventoryOps);
